import React, { useState } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import './BulkTalentModalSidebar.scss';

import { getBrandLists } from '../../Helpers/user_helpers';

import BulkTalentModalTalent from './Elements/BulkTalentModalTalent';
import SelectOption from '../General/SelectOption';
import InviteTalentOffPlatformModal from './InviteTalentOffPlatformModal';
import BulkTalentModalSidebarClearing from './Elements/BulkTalentModalSidebarClearing';

const VISIBLE_TALENT_BATCH_SIZE = 50;

const BulkTalentModalSidebar = props => {
  const { user, allTalent, selectedTalent, talentStatuses, outreachType, addNewTalentOffPlatform, isProcessingSend } = props;

  const [performanceImprovementForLongSelect, setPerformanceImprovementForLongSelect] = useState(false);
  const [inviteTalentOffPlatformModalOpen, setInviteTalentOffPlatformModalOpen] = useState(false);
  const [numVisibleTalent, setNumVisibleTalent] = useState(VISIBLE_TALENT_BATCH_SIZE);
  const openInviteTalentOffPlatformModal = () => setInviteTalentOffPlatformModalOpen(true);
  const closeInviteTalentOffPlatformModal = () => setInviteTalentOffPlatformModalOpen(false);

  const inviteOffTalentCallback = allTalentToAdd => {
    addNewTalentOffPlatform(allTalentToAdd);
    closeInviteTalentOffPlatformModal();
  };

  const selectNewTalentToAdd = data => props.selectNewTalent(data.value);
  const selectListToAdd = data => props.selectListToAdd(data.value);
  const brandCanInviteTalentOffPlatform = outreachType.allowOffPlatformInvites;

  const selectedTalentIds = new Set(selectedTalent.map(t => t.id));
  const talentOptions = _.orderBy(allTalent, 'name').map(user => ({
    value: user,
    label: user.name,
    disabled: selectedTalentIds.has(user.id)
  }));

  const lists = getBrandLists(user);
  const listOptions = _.orderBy(lists, 'createdAt', 'desc').map(list => ({
    value: list,
    label: list.title,
    sublabels: [`${list.users.length} member${list.users.length === 1 ? '' : 's'}`, `Created ${moment(list.createdAt).format('MMM Do, YY')}`],
    disabled: false
  }));

  const hasActions = !!selectedTalent.length;

  const visibleTalent = selectedTalent.slice(0, numVisibleTalent);
  const isShowMoreVisible = visibleTalent.length > 0 && numVisibleTalent < selectedTalent.length;

  return (
    <div className='bulk-talent-modal-sidebar'>
      <div className='header-controls'>
        <Select
          unstyled
          placeholder='Search Talent'
          classNamePrefix='search-select'
          onChange={selectNewTalentToAdd}
          className='search-select'
          isOptionDisabled={option => option.disabled}
          options={talentOptions}
          value={null}
          onInputChange={val => setPerformanceImprovementForLongSelect(val.length)}
          {...(talentOptions.length > 150 ? { menuIsOpen: performanceImprovementForLongSelect >= 2 } : {})}
          components={{ Option: SelectOption }}
        />
        <Select
          unstyled
          placeholder='From List'
          classNamePrefix='list-select'
          onChange={selectListToAdd}
          className='list-select'
          isOptionDisabled={option => option.disabled}
          options={listOptions}
          value={null}
          components={{ Option: SelectOption }}
        />
      </div>
      {hasActions && (
        <BulkTalentModalSidebarClearing
          user={user}
          selectedTalent={selectedTalent}
          setSelectedTalent={props.setSelectedTalent}
          updateStatusState={props.updateStatusState}
        />
      )}
      <div className='talent-results'>
        {!visibleTalent.length ? (
          <div className='empty-results'>
            {props.isFetchingTalent ? (
              <div className='message'>Getting Talent Details...</div>
            ) : (
              <div className='message'>Please search talent above or add from a list</div>
            )}
          </div>
        ) : (
          visibleTalent?.map(talent => {
            const status = talentStatuses[talent.id || talent.email] || (isProcessingSend ? 'progress' : null);
            return (
              <BulkTalentModalTalent
                key={talent?.id || talent?.email}
                user={user}
                talent={talent}
                analytics={props.analytics}
                status={status}
                removeTalentById={props.removeTalentById}
                removeTalentByOffPlatformEmail={props.removeTalentByOffPlatformEmail}
                outreachType={outreachType}
                openArtistModal={props.openArtistModal}
                openChatOverlay={props.openChatOverlay}
              />
            );
          })
        )}
        {isShowMoreVisible && (
          <div className='show-more-container' onClick={() => setNumVisibleTalent(numVisibleTalent + VISIBLE_TALENT_BATCH_SIZE)}>
            <div className='show-more'>
              View More
              <div className='show-more-count'>{selectedTalent.length - numVisibleTalent}</div>
            </div>
          </div>
        )}
        {brandCanInviteTalentOffPlatform && (
          <div className='invite-off-platform-talent' onClick={openInviteTalentOffPlatformModal}>
            <span className='message'>
              <FontAwesomeIcon icon={faPlus} />
              Invite Talent Not On Shopmy
            </span>
          </div>
        )}
      </div>
      {inviteTalentOffPlatformModalOpen && (
        <InviteTalentOffPlatformModal close={closeInviteTalentOffPlatformModal} saveCallback={inviteOffTalentCallback} />
      )}
    </div>
  );
};

BulkTalentModalSidebar.propTypes = {
  user: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  allTalent: PropTypes.array.isRequired,
  isFetchingTalent: PropTypes.bool.isRequired,
  selectedTalent: PropTypes.array.isRequired,
  removeTalentById: PropTypes.func.isRequired,
  removeTalentByOffPlatformEmail: PropTypes.func.isRequired,
  setSelectedTalent: PropTypes.func.isRequired,
  updateStatusState: PropTypes.func.isRequired,
  selectNewTalent: PropTypes.func.isRequired,
  selectListToAdd: PropTypes.func.isRequired,
  openArtistModal: PropTypes.func.isRequired,
  openChatOverlay: PropTypes.func.isRequired,
  outreachType: PropTypes.object.isRequired,
  addNewTalentOffPlatform: PropTypes.func.isRequired,
  isProcessingSend: PropTypes.bool.isRequired
};

export default BulkTalentModalSidebar;
